// DOM plumbing for the inline colour/font-size tools (inline-style-tools.js):
// recognising our formatting spans, applying one tool's change across an
// arbitrary selection Range, and normalising the DOM afterwards. Separate from
// the tool classes so the Editor.js wiring stays under its own roof.

/** One fs-* class from the token type scale (see FONT_SIZES). */
export const FS_CLASS_RE = /^fs-(?:xs|base|h[1-6]|d[1-4])$/;

/**
 * Is this one of OUR formatting spans (a colour or font-size span), as opposed
 * to the transient fake-background span Editor.js wraps the live selection in
 * while the inline toolbar is open? Ours carry a `color` style or an `fs-*`
 * class; the fake-background span carries only `background-color`. Everything
 * here must leave the latter alone — Editor.js removes it itself on toolbar
 * close, and touching it makes that removal misfire.
 */
export function isFormattingSpan(el) {
  if (!el || el.tagName !== "SPAN") return false;
  if (el.style && el.style.color) return true;
  for (const cls of el.classList) if (FS_CLASS_RE.test(cls)) return true;
  return false;
}

/** Remove a span that no longer carries any formatting, keeping its children. */
export function cleanupSpan(span) {
  if (span.getAttribute("style") === "") span.removeAttribute("style");
  if (span.getAttribute("class") === "") span.removeAttribute("class");
  if (span.attributes.length > 0) return;
  const parent = span.parentNode;
  if (!parent) return;
  while (span.firstChild) parent.insertBefore(span.firstChild, span);
  parent.removeChild(span);
}

/** Inline elements the normalise scope must climb out of: the scope has to sit
 * above every formatting span the edit can touch, or normalizeSpans/the
 * ancestor split can't see them. */
const INLINE_TAGS = new Set(["SPAN", "B", "STRONG", "I", "EM", "CODE", "MARK", "A"]);

/**
 * Apply one tool's change across a Range, however the range sits relative to
 * existing formatting spans (inside one, straddling one, covering several).
 * One path for every case:
 *
 * 1. extractContents() — the DOM splits any partially-covered span, and the
 *    halves left outside the range keep their formatting.
 * 2. strip() removes THIS tool's property from every span in the fragment
 *    (the other tool's property survives as a nested span).
 * 3. splitFormattingAncestorsAt() — when both selection ends were inside one
 *    formatting span, extractContents leaves that span in the tree and the
 *    collapsed range sits INSIDE it, so re-inserting would re-apply the very
 *    property strip just removed (or nest redundantly). Split each such
 *    ancestor at the point and land between the halves.
 * 4. When mutate is given (setting a value), the whole fragment is wrapped in
 *    one fresh span; when it's null (clearing), the stripped content goes back
 *    bare.
 * 5. normalizeSpans() sweeps the aftermath: shells emptied by the splits,
 *    parents shadowed by an only-child span, identical adjacent siblings,
 *    split text nodes.
 *
 * Returns the wrapper span if it's still in the DOM after normalising
 * (a merge may have dissolved it), else null.
 */
export function applyToRange(range, strip, mutate) {
  const container = range.commonAncestorContainer;
  const start = container instanceof Element ? container : container.parentElement;
  let scope = start;
  while (scope && scope.parentElement && INLINE_TAGS.has(scope.tagName)) {
    scope = scope.parentElement;
  }
  // Formatting inherited from ancestors the range sits WHOLLY inside.
  // extractContents never clones those (only nodes below the common ancestor),
  // and the ancestor split below severs them, so the fragment must carry the
  // inherited properties itself — minus this tool's, which strip() discards.
  let inheritedColor = "";
  let inheritedFs = "";
  for (let el = start; el && el !== scope; el = el.parentElement) {
    if (!isFormattingSpan(el)) continue;
    if (!inheritedColor && el.style.color) inheritedColor = el.style.color;
    if (!inheritedFs) inheritedFs = fsClass(el);
  }
  const fragment = range.extractContents();
  for (const span of fragment.querySelectorAll("span")) {
    strip(span);
    cleanupSpan(span);
  }
  if (inheritedColor || inheritedFs) {
    const carrier = document.createElement("span");
    if (inheritedColor) carrier.style.color = inheritedColor;
    if (inheritedFs) carrier.classList.add(inheritedFs);
    while (fragment.firstChild) carrier.appendChild(fragment.firstChild);
    fragment.appendChild(carrier);
    strip(carrier);
    cleanupSpan(carrier);
  }
  splitFormattingAncestorsAt(range, scope);
  let wrapper = null;
  if (mutate) {
    wrapper = document.createElement("span");
    mutate(wrapper);
    wrapper.appendChild(fragment);
    range.insertNode(wrapper);
  } else {
    range.insertNode(fragment);
  }
  if (scope) normalizeSpans(scope);
  return wrapper && wrapper.isConnected ? wrapper : null;
}

/**
 * Split every formatting-span ancestor of the (collapsed) range at the range
 * point, walking up to `scope`, and leave the range sitting between the
 * halves. Emptied halves are left for normalizeSpans to sweep. Non-formatting
 * inline ancestors (b/em/a…) are not split — our tools don't manage them, and
 * content inserted inside them should keep them.
 */
function splitFormattingAncestorsAt(range, scope) {
  let el =
    range.startContainer instanceof Element
      ? range.startContainer
      : range.startContainer.parentElement;
  while (el && el !== scope) {
    const parent = el.parentElement;
    if (isFormattingSpan(el)) {
      const tail = el.cloneNode(false);
      const tailRange = document.createRange();
      tailRange.setStart(range.startContainer, range.startOffset);
      tailRange.setEnd(el, el.childNodes.length);
      tail.appendChild(tailRange.extractContents());
      el.after(tail);
      range.setStartAfter(el);
      range.collapse(true);
    }
    el = parent;
  }
}

/** The element's fs-* class, or "". */
function fsClass(el) {
  for (const cls of el.classList) if (FS_CLASS_RE.test(cls)) return cls;
  return "";
}

/** Same colour + classes → the two spans are interchangeable. */
function sameFormatting(a, b) {
  return (
    a.style.cssText === b.style.cssText &&
    Array.from(a.classList).sort().join(" ") === Array.from(b.classList).sort().join(" ")
  );
}

/**
 * A span whose only content is another formatting span styles nothing the
 * child doesn't already control: push each property down (unless the child
 * overrides it) and let cleanupSpan unwrap the emptied parent.
 */
function hoistShadowedFormatting(parent) {
  const children = Array.from(parent.childNodes).filter(
    (n) => !(n.nodeType === Node.TEXT_NODE && n.data === "")
  );
  if (children.length !== 1) return;
  const child = children[0];
  if (!(child instanceof HTMLElement) || !isFormattingSpan(child)) return;
  if (parent.style.color) {
    if (!child.style.color) child.style.color = parent.style.color;
    parent.style.removeProperty("color");
  }
  const parentFs = fsClass(parent);
  if (parentFs) {
    if (!fsClass(child)) child.classList.add(parentFs);
    parent.classList.remove(parentFs);
  }
  cleanupSpan(parent);
}

/**
 * Tidy formatting spans under `scope` after a range edit: drop empty shells
 * the extractContents() split left behind, unwrap shadowed parents, merge
 * identical adjacent siblings, and rejoin split text nodes. Only OUR spans are
 * touched (see isFormattingSpan) — the fake-background span passes through.
 */
function normalizeSpans(scope) {
  for (const span of scope.querySelectorAll("span")) {
    if (!span.isConnected || !isFormattingSpan(span)) continue;
    if (!span.textContent && !span.querySelector("br")) {
      span.remove();
      continue;
    }
    hoistShadowedFormatting(span);
  }
  for (const span of scope.querySelectorAll("span")) {
    if (!span.isConnected || !isFormattingSpan(span)) continue;
    let next = span.nextSibling;
    while (next && next.nodeType === Node.TEXT_NODE && next.data === "") {
      const emptyText = next;
      next = next.nextSibling;
      emptyText.remove();
    }
    if (
      next instanceof HTMLElement &&
      isFormattingSpan(next) &&
      sameFormatting(span, next)
    ) {
      while (next.firstChild) span.appendChild(next.firstChild);
      next.remove();
    }
  }
  scope.normalize();
}
